import { useQuery } from "@tanstack/react-query";
import { type JSX, Suspense, lazy } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router";

import { NavBadgeError } from "@/components/sidebar/nav-badge-error";
import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { feedbackApi } from "@/lib/feedback";
import { knowledgeFindingsApi } from "@/lib/knowledge-findings";
import { SOURCES_SUBTABS, isValidSourcesSubTab, settingsTabsForRole } from "@/lib/navigation";
import { selectedRepositoryId } from "@/lib/repository-scope";
import { sensitiveDataApi, sensitiveDataKeys } from "@/lib/sensitive-data";
import { Role } from "@/lib/supabase";
import { useKnowledgeRepositorySelection } from "@/lib/use-knowledge-repository-selection";
import { cn } from "@/lib/utils";
import { VALIDATION_STATS_QUERY_KEY, getValidationStats } from "@/lib/validation";
import { useSourceCounts } from "@/routes/sources/use-source-counts";

/** Shared with feedback detail invalidation — keep key identity stable. */
export const FEEDBACK_STATS_QUERY_KEY = ["feedback", "stats"] as const;

// Lazy so the benchmark-status chunk stays split off the settings-nav-panel
// bundle, same as when it was mounted standalone by `AppShell`.
const BenchmarkSidebarStatus = lazy(() =>
  import("@/components/sidebar/benchmark-status").then((module) => ({
    default: module.BenchmarkSidebarStatus,
  })),
);

export interface SettingsNavPanelProps {
  role: Role;
  onNavigate?: () => void;
  /** Gates the benchmark indicator's polling, same as the sidebar-open gate
   * the standalone block used before it moved inline (see design.md's
   * "Revision 2026-09-18"). Defaults to `true` for callers that don't track
   * a collapsible sidebar. */
  sidebarOpen?: boolean;
}

/**
 * Renders a nav-link count badge plus its screen-reader-only echo, or
 * nothing when `count` is not positive. Two render modes back the two
 * markups the sidebar actually used before this helper existed — a
 * `<Badge>` (feedback's pending/active counters) and a plain rounded
 * `<span>` (knowledge/quality's counters) — kept distinct because the two
 * markups are not visually identical (`Badge`'s `variantClasses` use the
 * `brand-alert-*` tokens, the span uses `bg-destructive`); collapsing them
 * into one would change what renders, which the callers below don't want.
 */
function NavCountBadge({
  count,
  srText,
  className,
  render,
  badgeVariant = "destructive",
  title,
}: {
  count: number;
  srText: string;
  className: string;
  render: "badge" | "span";
  badgeVariant?: BadgeVariant;
  /** Tooltip for sighted users when the number alone does not say what it counts. */
  title?: string;
}): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <>
      {render === "badge" ? (
        <Badge variant={badgeVariant} className={className} title={title} aria-hidden="true">
          {count}
        </Badge>
      ) : (
        <span aria-hidden="true" className={className}>
          {count}
        </span>
      )}
      <span className="sr-only">{srText}</span>
    </>
  );
}

function SourcesSubNav({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const activeSourceType = isValidSourcesSubTab(typeParam) ? typeParam : "upload";
  const repositoryId = selectedRepositoryId(searchParams.toString());
  const tabCount = useSourceCounts(repositoryId ?? "default");

  return (
    <div className="ml-2 flex flex-col gap-0.5 border-l border-border pl-2">
      {SOURCES_SUBTABS.map((sub) => {
        const active = activeSourceType === sub.value;
        const targetParams = new URLSearchParams(searchParams);
        targetParams.set("type", sub.value);
        targetParams.delete("highlight");
        return (
          <NavLink
            key={sub.value}
            to={`/settings/sources?${targetParams.toString()}`}
            onClick={onNavigate}
            className={cn(
              "rounded-md px-2 py-1 text-left text-body-3 transition-colors",
              active
                ? "bg-muted/80 font-semibold text-foreground"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
            )}
            aria-current={active ? "page" : undefined}
          >
            <span className="inline-flex items-center gap-2">
              {sub.label}
              <Badge variant="muted">{tabCount(sub.value)}</Badge>
            </span>
          </NavLink>
        );
      })}
    </div>
  );
}

export function SettingsNavPanel({
  role,
  onNavigate,
  sidebarOpen = true,
}: SettingsNavPanelProps): JSX.Element | null {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Raw, unvalidated — used only to build nav-link targets below (which must
  // mirror whatever the URL already carries, valid or not: the page it links
  // to is responsible for correcting itself once it mounts).
  const repositoryScopeKey = selectedRepositoryId(searchParams.toString()) ?? "default";
  // Validated — the sidebar mounts on every /settings/* page, including ones
  // that never otherwise call this hook (e.g. /settings/users), so it's the
  // only thing that both (a) knows whether a `?repository=` id names a real
  // repository and (b) self-corrects the URL when it doesn't. Without it,
  // `validationStatsQuery` below would poll a dead repository forever (its
  // `queryFn` reads the ambient `?repository=` off the URL directly, not off
  // a query-key value). Only fetches the repository list at all when there's
  // an id in the URL to validate in the first place — the common case (no
  // `?repository=` param, which is every page today while `multi_kr_enabled`
  // is off) costs nothing extra.
  const repositorySelection = useKnowledgeRepositorySelection({
    includeArchived: true,
    enabled: repositoryScopeKey !== "default",
  });
  const visibleTabs = settingsTabsForRole(role);
  const isAdmin = role === Role.Admin;
  const canValidate = role === Role.Contributor || isAdmin;
  const sourcesActive =
    location.pathname === "/settings/sources" || location.pathname.startsWith("/settings/sources/");

  const statsQuery = useQuery({
    queryKey: FEEDBACK_STATS_QUERY_KEY,
    queryFn: () => feedbackApi.stats(),
    enabled: isAdmin,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const pendingCount = statsQuery.data?.pending_count ?? 0;
  const activeCount = statsQuery.data?.active_count ?? 0;
  const validationStatsQuery = useQuery({
    queryKey: [...VALIDATION_STATS_QUERY_KEY, repositorySelection.scopeKey],
    queryFn: getValidationStats,
    enabled: canValidate && repositorySelection.isReady,
    staleTime: 60_000,
    refetchInterval: (query) =>
      query.state.data?.relevance_backfill.complete === false ? 5_000 : 30_000,
  });
  // `?? 0` would make a genuine "nothing pending" indistinguishable from
  // "the count failed to load" (and hide the failure instead of surfacing
  // it) — only trust a successful response for the number, and flag a
  // failure separately below.
  const highRelevancePendingCount = validationStatsQuery.isSuccess
    ? validationStatsQuery.data.high_relevance_pending_count
    : 0;
  // Query key composition is deliberate: `knowledge-quality.tsx`'s
  // `countsQuery` keys on `["knowledge-findings", "counts", scopeKey,
  // verdictFilter]`, and `verdictFilter` defaults to `"all"` (its
  // `queryFn` then requests `verdict: undefined`, same as here). Matching
  // that literally lets the two dedupe into a single request whenever the
  // quality page is open with no verdict filter selected; if the page's
  // filter changes to something else, its key diverges and this query keeps
  // running independently — still correct, just no longer deduped.
  const findingsCountsQuery = useQuery({
    queryKey: ["knowledge-findings", "counts", repositorySelection.scopeKey, "all"] as const,
    queryFn: () => knowledgeFindingsApi.counts({ verdict: undefined }),
    enabled: isAdmin && repositorySelection.isReady,
    staleTime: 60_000,
  });
  // Same rationale as `highRelevancePendingCount` above — only a successful
  // response should report a number; a failure must surface as an error
  // indicator, not silently read as "zero open findings".
  const openFindingsCount = findingsCountsQuery.isSuccess ? findingsCountsQuery.data.open : 0;
  // Open secrets of the "Seguridad" tab (`add-sensitive-data-review`). Kept apart from the
  // findings count above because they measure different things (duplicate or contradictory
  // documents versus leaked credentials), and summing them would hide which one needs attention.
  // The key sits under the sensitive-data root, so an action in the tab (which invalidates that
  // root) refreshes this count too; when the tab is open with no `?repository=` it is also the
  // tab's own key and the two share one request.
  const sensitiveSummaryQuery = useQuery({
    queryKey: sensitiveDataKeys.summary(repositorySelection.scopeKey),
    queryFn: () => sensitiveDataApi.summary(),
    enabled: isAdmin && repositorySelection.isReady,
    staleTime: 60_000,
  });
  const openSecretsCount = sensitiveSummaryQuery.isSuccess
    ? sensitiveSummaryQuery.data.open_secrets
    : 0;

  // A viewer has no settings tabs (they reach /settings/sources by link): no
  // empty labelled landmark for a screen reader to announce.
  if (visibleTabs.length === 0) return null;

  return (
    <nav aria-label="Settings" className="flex flex-col gap-0.5 px-2 py-3">
      {visibleTabs.map((tab) => {
        const isSources = tab.to === "/settings/sources";
        const carriesRepository =
          repositoryScopeKey !== "default" &&
          (isSources ||
            tab.to === "/settings/knowledge" ||
            tab.to === "/settings/knowledge-quality");
        const tabTarget = carriesRepository ? `${tab.to}?repository=${repositoryScopeKey}` : tab.to;
        return (
          <div key={tab.to} className="flex flex-col gap-0.5">
            <NavLink
              to={tabTarget}
              end={!isSources && tab.nested !== true}
              // Fuentes expands nested type subtabs — keep the drawer open
              // on mobile until the user picks one of those leaves.
              onClick={isSources ? undefined : onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center rounded-md px-3 py-1.5 text-left text-body-3 transition-colors",
                  isActive || (isSources && sourcesActive)
                    ? "bg-muted font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                )
              }
            >
              <tab.icon
                aria-hidden="true"
                className="mr-1.5 size-3.5 shrink-0"
                strokeWidth={1.75}
              />
              {tab.label}
              {tab.to === "/settings/benchmarks" && (
                <Suspense fallback={null}>
                  <BenchmarkSidebarStatus open={sidebarOpen} />
                </Suspense>
              )}
              {tab.to === "/settings/feedback" && (
                <NavCountBadge
                  count={pendingCount}
                  render="badge"
                  badgeVariant="destructive"
                  className="ml-1.5 h-4 min-w-[1rem] justify-center px-1 py-0 text-label-3"
                  srText={`. ${pendingCount} feedback ${pendingCount === 1 ? "pendiente" : "pendientes"}`}
                />
              )}
              {tab.to === "/settings/feedback" && (
                <NavCountBadge
                  count={activeCount}
                  render="badge"
                  badgeVariant="warning"
                  className="ml-1 h-4 min-w-[1rem] justify-center px-1 py-0 text-label-3"
                  srText={`. ${activeCount} feedback en trabajo activo`}
                />
              )}
              {tab.to === "/settings/knowledge" && (
                <NavCountBadge
                  count={highRelevancePendingCount}
                  render="span"
                  className="ml-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-label-3 font-medium text-destructive-foreground"
                  srText={`. ${highRelevancePendingCount} caso${
                    highRelevancePendingCount === 1 ? "" : "s"
                  } pendiente${highRelevancePendingCount === 1 ? "" : "s"} de relevancia alta`}
                />
              )}
              {tab.to === "/settings/knowledge" && validationStatsQuery.isError && (
                <NavBadgeError
                  title="No se pudo cargar el conteo de relevancia alta"
                  srText=". No se pudo cargar el conteo de casos pendientes de relevancia alta"
                />
              )}
              {tab.to === "/settings/knowledge-quality" && (
                <NavCountBadge
                  count={openFindingsCount}
                  render="span"
                  className="ml-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-label-3 font-medium text-destructive-foreground"
                  srText={`. ${openFindingsCount} hallazgo${openFindingsCount === 1 ? "" : "s"} abierto${
                    openFindingsCount === 1 ? "" : "s"
                  }`}
                />
              )}
              {tab.to === "/settings/knowledge-quality" && findingsCountsQuery.isError && (
                <NavBadgeError
                  title="No se pudo cargar el conteo de hallazgos abiertos"
                  srText=". No se pudo cargar el conteo de hallazgos abiertos"
                />
              )}
              {tab.to === "/settings/knowledge-quality" && (
                <NavCountBadge
                  count={openSecretsCount}
                  render="badge"
                  badgeVariant="warning"
                  title="Secretos abiertos"
                  className="ml-1 h-4 min-w-[1rem] justify-center px-1 py-0 text-label-3"
                  srText={`. ${openSecretsCount} secreto${openSecretsCount === 1 ? "" : "s"} abierto${
                    openSecretsCount === 1 ? "" : "s"
                  }`}
                />
              )}
              {tab.to === "/settings/knowledge-quality" && sensitiveSummaryQuery.isError && (
                <NavBadgeError
                  title="No se pudo cargar el conteo de secretos abiertos"
                  srText=". No se pudo cargar el conteo de secretos abiertos"
                />
              )}
            </NavLink>
            {isSources && sourcesActive ? (
              <SourcesSubNav {...(onNavigate !== undefined ? { onNavigate } : {})} />
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}
